import { useSyncExternalStore } from "react";

// Collapse is remembered for the session, not just while the menu is open: the
// menu unmounts on close, so component state would forget every toggle the
// moment the popover shut. Module scope is deliberate: it is shared by every
// selector instance, keyed by group id.
const collapsedGroupIds = new Set<string>();
const listeners = new Set<() => void>();

// useSyncExternalStore compares snapshots by identity, so the Set is cloned on
// every mutation rather than mutated in place.
let snapshot: ReadonlySet<string> = new Set(collapsedGroupIds);

function emit() {
  snapshot = new Set(collapsedGroupIds);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ReadonlySet<string> {
  return snapshot;
}

export function useCollapsedGroups(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Known limitation: keys are bare group ids, shared across every selector in
// the app. Two unrelated menus that both carry a group with the same id share
// its collapsed flag. Namespacing needs a scope that survives the menu
// unmounting, which is the whole point of this store, and neither `useId`
// nor the group list provides one, so the collision is accepted rather than
// half-fixed. In practice ids are brand-group record ids or names, which are
// unique per admin.

export function toggleGroupCollapsed(groupId: string) {
  if (collapsedGroupIds.has(groupId)) {
    collapsedGroupIds.delete(groupId);
  } else {
    collapsedGroupIds.add(groupId);
  }
  emit();
}

export function setGroupCollapsed(groupId: string, collapsed: boolean) {
  if (collapsed === collapsedGroupIds.has(groupId)) return;
  if (collapsed) {
    collapsedGroupIds.add(groupId);
  } else {
    collapsedGroupIds.delete(groupId);
  }
  emit();
}

/** Test and story hook: module state outlives a single render tree. */
export function resetCollapsedGroups() {
  if (collapsedGroupIds.size === 0) return;
  collapsedGroupIds.clear();
  emit();
}
